// SiteViewer3D.tsx — Forked from PanelViewer3D for site-view 3D rendering
// Renders a single panel as an extruded 3D concrete plate with openings,
// features, and embed markers. Accepts panel as a prop (no store coupling).
// Stripped of entity selection/editing — view-only with full camera controls.

import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { Grid, Environment, Html } from '@react-three/drei';
import { useRef, useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import { useShallowStore } from '../store/useStore';
import type { PanelData, Elevation, ViewPreset, ShadingMode, ClashResult } from '../types/panelvar';
import type { WallSegment } from './SiteViewport3D';
import NorthArrow from './viewer/NorthArrow';
import GridLabels, { snapGridExtent } from './viewer/GridLabels';
import PanelMesh3D from './viewer/PanelMesh3D';
import { computeEdgeTrims } from '../utils/buildingFootprint';

// ── Camera preset directions (unit-ish vectors, normalised in goToPreset) ───
const PRESET_DIR: Record<ViewPreset, [number, number, number]> = {
  'front':   [0,    0,     1],
  'back':    [0,    0,    -1],
  'left':    [-1,   0,     0],
  'right':   [1,    0,     0],
  'top':     [0,    1,     0.001],
  'bottom':  [0,   -1,     0.001],
  'br-iso':  [1,    0.67, -1],
  'bl-iso':  [-1,   0.67, -1],
  'fr-iso':  [1,    0.67,  1],
  'fl-iso':  [-1,   0.67,  1],
};

// Fit distance so the visible extent = 75% of max wall width from center
function fitDistance(viewW: number, viewH: number, fovDeg = 45, aspect = 1): number {
  const halfTan = Math.tan((fovDeg * Math.PI / 180) / 2);
  return Math.max((viewH / 2) / halfTan, (viewW / 2) / (aspect * halfTan)) * 1.5;
}

// Compute projected visible width/height for a given camera direction and 3D bounding extents.
// Uses max(sx, sz) for ALL views so the camera stays at 75% of the max wall width
// from center regardless of viewing angle — prevents L/R views from zooming too close.
function projectedSize(dir: [number, number, number], sx: number, sy: number, sz: number): { w: number; h: number } {
  const [dx, dy, dz] = dir;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
  const ay = Math.abs(dy / len);
  const maxDim = Math.max(sx, sz);

  if (ay > 0.5) {
    // Top / bottom — looking down Y
    return { w: maxDim, h: maxDim };
  }
  // All side views (front, back, left, right, iso)
  return { w: maxDim, h: sy };
}

// ── CameraLock — zero-drift camera with raw pointer orbit/zoom ──────────────
interface CamState {
  theta: number;
  phi: number;
  dist: number;
  near: number;
  far: number;
  tx: number;
  ty: number;
  tz: number;
}

function sphericalToCartesian(theta: number, phi: number, dist: number): [number, number, number] {
  const sp = Math.sin(phi);
  return [
    dist * sp * Math.sin(theta),
    dist * Math.cos(phi),
    dist * sp * Math.cos(theta),
  ];
}

function CameraLock({
  preset,
  panelId,
  panelSize,
  center,
  resetKey,
  zoomRef,
  panRef,
  onManualMove,
  onPanelClick,
  onPanelDoubleClick,
}: {
  preset: ViewPreset | null;
  panelId: string | undefined;
  panelSize: React.MutableRefObject<{ sx: number; sy: number; sz: number }>;
  center: React.MutableRefObject<{ x: number; y: number; z: number }>;
  resetKey: number;
  zoomRef: React.MutableRefObject<((factor: number) => void) | null>;
  panRef: React.MutableRefObject<((dx: number, dy: number) => void) | null>;
  onManualMove?: () => void;
  onPanelClick?: (panelId: string, ctrlKey: boolean) => void;
  onPanelDoubleClick?: (panelId: string) => void;
}) {
  const { camera, gl, invalidate, scene } = useThree();
  const state = useRef<CamState>({ theta: 0, phi: Math.PI / 2, dist: 500, near: 0.1, far: 1_000_000, tx: 0, ty: 0, tz: 0 });
  const dragging = useRef(false);
  const dragMode = useRef<'none' | 'orbit' | 'pan' | 'zoom'>('none');
  const lastPointer = useRef({ x: 0, y: 0 });
  const downPointer = useRef<{ x: number; y: number; ctrlKey: boolean } | null>(null);
  const lastClickTime = useRef(0);
  const lastClickId = useRef<string | null>(null);
  const raycaster = useRef(new THREE.Raycaster());

  // Expose zoom function to parent
  useEffect(() => {
    zoomRef.current = (factor: number) => {
      state.current.dist = Math.max(1, Math.min(1_000_000, state.current.dist * factor));
      invalidate();
    };
    return () => { zoomRef.current = null; };
  }, [zoomRef, invalidate]);

  // Expose pan function to parent
  useEffect(() => {
    panRef.current = (dx: number, dy: number) => {
      const s = state.current;
      const panSpeed = s.dist * 0.002;
      const rx = Math.cos(s.theta), rz = -Math.sin(s.theta);
      const cp = Math.cos(s.phi);
      const sp = Math.sin(s.phi);
      const ux = -cp * Math.sin(s.theta), uy = sp, uz = -cp * Math.cos(s.theta);
      s.tx += (-dx * rx + dy * ux) * panSpeed;
      s.ty += dy * uy * panSpeed;
      s.tz += (-dx * rz + dy * uz) * panSpeed;
      invalidate();
    };
    return () => { panRef.current = null; };
  }, [panRef, invalidate]);

  // Compute spherical from preset direction vector
  useEffect(() => {
    if (!preset) return;
    const { sx, sy, sz } = panelSize.current;
    if (!sx && !sy && !sz) return;

    const cam = camera as THREE.PerspectiveCamera;
    const el = gl.domElement;
    const aspect = el.clientWidth > 0 && el.clientHeight > 0
      ? el.clientWidth / el.clientHeight
      : (cam.aspect > 0 ? cam.aspect : 1);
    const dir = PRESET_DIR[preset];
    const { w, h } = projectedSize(dir, sx, sy, sz);
    const dist = fitDistance(w, h, cam.fov ?? 45, aspect);
    if (!Number.isFinite(dist) || dist <= 0) return;

    const [dx, dy, dz] = PRESET_DIR[preset];
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
    const nx = dx / len, ny = dy / len, nz = dz / len;

    const theta = Math.atan2(nx, nz);
    const phi = Math.acos(Math.max(-1, Math.min(1, ny)));

    const c = center.current;
    state.current = {
      theta,
      phi: Math.max(0.01, Math.min(Math.PI - 0.01, phi)),
      dist,
      near: dist * 0.001,
      far: dist * 50,
      tx: c.x, ty: c.y, tz: c.z,
    };

    const [px, py, pz] = sphericalToCartesian(theta, phi, dist);
    camera.position.set(px + c.x, py + c.y, pz + c.z);
    camera.lookAt(c.x, c.y, c.z);
    cam.near = state.current.near;
    cam.far = state.current.far;
    cam.updateProjectionMatrix();
    invalidate();
  }, [preset, panelId, resetKey, camera, gl, panelSize, center, invalidate]);

  // Enforce camera position every frame
  useFrame(() => {
    const s = state.current;
    const [ox, oy, oz] = sphericalToCartesian(s.theta, s.phi, s.dist);
    const px = ox + s.tx, py = oy + s.ty, pz = oz + s.tz;
    camera.position.set(px, py, pz);
    camera.lookAt(s.tx, s.ty, s.tz);
  });

  // Mouse controls: orbit (right/shift+left), pan (middle), zoom (wheel/ctrl+left)
  useEffect(() => {
    const el = gl.domElement;
    const inv = invalidate;

    const onPointerDown = (e: PointerEvent) => {
      if (e.button === 0) {
        // Left click — reserved for panel selection (no orbit)
        downPointer.current = { x: e.clientX, y: e.clientY, ctrlKey: e.ctrlKey || e.metaKey };
        lastPointer.current = { x: e.clientX, y: e.clientY };
      }
      if (e.button === 1) {
        // Middle click — pan
        e.preventDefault();
        dragging.current = true;
        dragMode.current = 'pan';
        lastPointer.current = { x: e.clientX, y: e.clientY };
        el.setPointerCapture(e.pointerId);
      }
      if (e.button === 2) {
        // Right click — orbit
        dragging.current = true;
        dragMode.current = 'orbit';
        lastPointer.current = { x: e.clientX, y: e.clientY };
        el.setPointerCapture(e.pointerId);
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!dragging.current) return;
      const mdx = e.clientX - lastPointer.current.x;
      const mdy = e.clientY - lastPointer.current.y;
      lastPointer.current = { x: e.clientX, y: e.clientY };

      const s = state.current;
      if (dragMode.current === 'orbit') {
        s.theta -= mdx * 0.005;
        s.phi = Math.max(0.01, Math.min(Math.PI - 0.01, s.phi + mdy * 0.005));
        onManualMove?.();
      } else if (dragMode.current === 'pan') {
        const panSpeed = s.dist * 0.002;
        const rx = Math.cos(s.theta), rz = -Math.sin(s.theta);
        const cp = Math.cos(s.phi), sp = Math.sin(s.phi);
        const ux = -cp * Math.sin(s.theta), uy = sp, uz = -cp * Math.cos(s.theta);
        s.tx += (-mdx * rx + mdy * ux) * panSpeed;
        s.ty += mdy * uy * panSpeed;
        s.tz += (-mdx * rz + mdy * uz) * panSpeed;
        onManualMove?.();
      }
      inv();
    };

    const onPointerUp = (e: PointerEvent) => {
      const dp = downPointer.current;
      const wasDragging = dragging.current;
      dragging.current = false;
      dragMode.current = 'none';
      downPointer.current = null;
      try { el.releasePointerCapture(e.pointerId); } catch {/* ignore */}

      // If left button released without dragging → this is a click
      if (e.button === 0 && !wasDragging && dp) {
        const dx = Math.abs(e.clientX - dp.x);
        const dy = Math.abs(e.clientY - dp.y);
        if (dx <= 4 && dy <= 4) {
          // Raycast to find panel
          const rect = el.getBoundingClientRect();
          const ndcX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
          const ndcY = -((e.clientY - rect.top) / rect.height) * 2 + 1;
          raycaster.current.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
          const hits = raycaster.current.intersectObjects(scene.children, true);
          let hitPanelId: string | null = null;
          for (const hit of hits) {
            hitPanelId = findPanelId(hit.object);
            if (hitPanelId) break;
          }
          if (hitPanelId) {
            const now = Date.now();
            if (now - lastClickTime.current < 400 && lastClickId.current === hitPanelId) {
              onPanelDoubleClick?.(hitPanelId);
              lastClickTime.current = 0;
              lastClickId.current = null;
            } else {
              onPanelClick?.(hitPanelId, dp.ctrlKey);
              lastClickTime.current = now;
              lastClickId.current = hitPanelId;
            }
          }
        }
      }
    };

    const onWheel = (e: WheelEvent) => {
      const s = state.current;
      const factor = e.deltaY > 0 ? 1.1 : 0.9;
      s.dist = Math.max(1, Math.min(1_000_000, s.dist * factor));
      onManualMove?.();
      inv();
    };

    const onContextMenu = (e: Event) => e.preventDefault();
    const onAuxClick = (e: MouseEvent) => { if (e.button === 1) e.preventDefault(); };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);
    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('contextmenu', onContextMenu);
    el.addEventListener('auxclick', onAuxClick);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('contextmenu', onContextMenu);
      el.removeEventListener('auxclick', onAuxClick);
    };
  }, [gl, invalidate, onManualMove, camera, scene, onPanelClick, onPanelDoubleClick]);

  return null;
}

// ── Viewport toolbar (view presets + shading) ────────────────────────────────
function SiteViewerToolbar({ onCenterView, onZoomIn, onZoomOut, onPanLeft, onPanRight, onPanUp, onPanDown }: {
  onCenterView: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onPanLeft: () => void;
  onPanRight: () => void;
  onPanUp: () => void;
  onPanDown: () => void;
}) {
  const { ui, setViewPreset, setShadingMode, toggleGrid, toggleGround, togglePanelMarks } = useShallowStore(s => ({
    ui:            s.ui,
    setViewPreset: s.setViewPreset,
    setShadingMode:s.setShadingMode,
    toggleGrid:    s.toggleGrid,
    toggleGround:  s.toggleGround,
    togglePanelMarks: s.togglePanelMarks,
  }));

  const pb = (id: ViewPreset, label: string, title: string) => (
    <button key={id} className={`vt-btn${ui.viewPreset === id ? ' active' : ''}`} onClick={() => setViewPreset(id)} title={title}>{label}</button>
  );

  return (
    <div className="viewport-toolbar">
      <div className="vt-grid-3x3">
        {pb('bl-iso', '\u25CF', 'Back-Left Iso')}{pb('back', 'B', 'Back')}{pb('br-iso', '\u25CF', 'Back-Right Iso')}
        {pb('left', 'L', 'Left')}<button className={`vt-btn${ui.viewPreset === null ? ' active' : ''}`} onClick={onCenterView} title="Zoom Extents">{'\u25A0'}</button>{pb('right', 'R', 'Right')}
        {pb('fl-iso', '\u25CF', 'Front-Left Iso')}{pb('front', 'F', 'Front')}{pb('fr-iso', '\u25CF', 'Front-Right Iso')}
      </div>
      <div className="vt-group">
        {pb('top', 'TP', 'Top')}{pb('bottom', 'BT', 'Bottom')}
      </div>
      <div className="vt-group">
        <button className="vt-btn" onClick={onZoomIn} title="Zoom In">+</button>
        <button className="vt-btn" onClick={onZoomOut} title="Zoom Out">−</button>
        <span className="vt-sep">│</span>
        <button className="vt-btn" onClick={onPanLeft} title="Pan Left">◀</button>
        <button className="vt-btn" onClick={onPanRight} title="Pan Right">▶</button>
        <button className="vt-btn" onClick={onPanUp} title="Pan Up">▲</button>
        <button className="vt-btn" onClick={onPanDown} title="Pan Down">▼</button>
      </div>
      <div className="vt-group vt-shading">
        {(['wireframe', 'hidden', 'shaded'] as ShadingMode[]).map(m => (
          <button key={m} className={`vt-btn${ui.shadingMode === m ? ' active' : ''}`} onClick={() => setShadingMode(m)}>
            {m.charAt(0).toUpperCase() + m.slice(1)}
          </button>
        ))}
      </div>
      <div className="vt-group">
        <button className={`vt-btn${ui.showGrid ? ' active' : ''}`} onClick={toggleGrid} title="Toggle grid">Grid</button>
        <button className={`vt-btn${ui.showGround ? ' active' : ''}`} onClick={toggleGround} title="Toggle ground plane">Ground</button>
        <button className={`vt-btn${ui.showPanelMarks ? ' active' : ''}`} onClick={togglePanelMarks} title="Toggle panel marks">Marks</button>
      </div>
    </div>
  );
}

// ── Compute local panel offsets for one wall segment ─────────────────────────
function segmentLayout(panels: PanelData[], jointWidth: number, leftEndJoint: number, rightEndJoint: number) {
  const offsets: number[] = [];
  let x = leftEndJoint;
  let maxH = 0;
  for (let i = 0; i < panels.length; i++) {
    offsets.push(x);
    const gap = i < panels.length - 1 ? jointWidth : rightEndJoint;
    x += panels[i].mpvar.width + gap;
    maxH = Math.max(maxH, panels[i].mpvar.height);
  }
  const totalW = panels.length > 0 ? x - rightEndJoint : 0;
  return { offsets, totalW, maxH: maxH || 168 };
}

// ── Raycaster helper — walk up scene graph to find panelId ────────────────────
function findPanelId(obj: THREE.Object3D): string | null {
  let cur: THREE.Object3D | null = obj;
  while (cur) {
    if (cur.userData?.panelId) return cur.userData.panelId as string;
    cur = cur.parent;
  }
  return null;
}

// ── Clash beacon — pulsing red point light + sphere marker ───────────────────
const CLASH_ERROR_COLOR = new THREE.Color(0xff0000);
const CLASH_WARN_COLOR  = new THREE.Color(0xff4400);

function ClashBeacon({ x, y, z, severity, description }: { x: number; y: number; z: number; severity: 'error' | 'warning'; description: string }) {
  const lightRef = useRef<THREE.PointLight>(null);
  const matRef   = useRef<THREE.MeshBasicMaterial>(null);
  const [hovered, setHovered] = useState(false);
  const color = severity === 'error' ? CLASH_ERROR_COLOR : CLASH_WARN_COLOR;
  const baseIntensity = severity === 'error' ? 5 : 3;

  useFrame(({ clock, invalidate }) => {
    // Pulsing sin wave: 0.5–1.0 range, ~2 Hz
    const pulse = 0.75 + 0.25 * Math.sin(clock.elapsedTime * 4 * Math.PI);
    if (lightRef.current) lightRef.current.intensity = baseIntensity * pulse;
    if (matRef.current) matRef.current.opacity = 0.5 + 0.5 * pulse;
    invalidate(); // request next frame — canvas is frameloop="demand"
  });

  return (
    <group position={[x, y, z]}>
      <pointLight ref={lightRef} color={color} intensity={baseIntensity} distance={600} decay={2} />
      <mesh
        onPointerEnter={(e) => { e.stopPropagation(); setHovered(true); }}
        onPointerLeave={() => setHovered(false)}
      >
        <sphereGeometry args={[8, 16, 16]} />
        <meshBasicMaterial ref={matRef} color={color} transparent opacity={0.8} depthWrite={false} />
      </mesh>
      {hovered && (
        <Html center distanceFactor={400} style={{ pointerEvents: 'none' }}>
          <div style={{
            background: severity === 'error' ? '#cc0000' : '#cc4400',
            color: 'white', padding: '6px 10px', borderRadius: 4,
            fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
          }}>
            {description}
          </div>
        </Html>
      )}
    </group>
  );
}

// ── Main exported component ───────────────────────────────────────────────────
interface SiteViewer3DProps {
  segments: WallSegment[];
  elevations?: Elevation[];
  jointWidth?: number;
  floorVertices?: [number, number][];
  /** Y elevations (inches) of inter-story slabs above ground for multi-floor buildings */
  upperSlabHeights?: number[];
  /** Y elevation (inches) of the roof line (top of last floor's panels) */
  roofHeight?: number;
  selectedPanelIds?: string[];
  onPanelClick?: (panelId: string, ctrlKey: boolean) => void;
  onPanelDoubleClick?: (panelId: string) => void;
  clashes?: ClashResult[];
}

export default function SiteViewer3D({ segments, elevations = [], jointWidth = 0.75, floorVertices, upperSlabHeights: _upperSlabHeights, roofHeight: _roofHeight, selectedPanelIds = [], onPanelClick, onPanelDoubleClick, clashes = [] }: SiteViewer3DProps) {
  const { ui, setViewPreset, clearViewPreset } = useShallowStore(s => ({
    ui: s.ui,
    setViewPreset: s.setViewPreset,
    clearViewPreset: s.clearViewPreset,
  }));

  const selectedSet = useMemo(() => new Set(selectedPanelIds), [selectedPanelIds]);

  // Collect panel IDs involved in any clash for red-tinting
  const clashPanelIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of clashes) c.panelIds.forEach(id => ids.add(id));
    return ids;
  }, [clashes]);

  // Lookup: elevId → Elevation (for computing joint trims per panel)
  const elevMap = useMemo(() => new Map(elevations.map(e => [e.id, e])), [elevations]);

  const [resetKey, setResetKey] = useState(0);
  const lastPresetRef = useRef<ViewPreset>('fl-iso');
  if (ui.viewPreset) lastPresetRef.current = ui.viewPreset;
  const zoomRef = useRef<((factor: number) => void) | null>(null);
  const panRef = useRef<((dx: number, dy: number) => void) | null>(null);

  // Compute bounding box center across all wall segments for camera targeting
  const allPanels = segments.flatMap(s => s.panels);
  const totalPanelCount = allPanels.length;

  const { layouts, boundingCenter, boundingSize } = useMemo(() => {
    const lays = segments.map(seg => segmentLayout(seg.panels, jointWidth, seg.leftEndJoint, seg.rightEndJoint));

    // Compute bounding box across all segments in world space
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    let maxH = 0;
    segments.forEach((seg, si) => {
      const lay = lays[si];
      if (seg.panels.length === 0) return;
      // Four corners of this wall in world space: (0, 0) and (totalW, 0) in local, rotated + translated
      const cos = Math.cos(seg.rotationY);
      const sin = -Math.sin(seg.rotationY);
      const x0 = seg.x;
      const z0 = seg.z;
      const x1 = seg.x + cos * lay.totalW;
      const z1 = seg.z + sin * lay.totalW;
      minX = Math.min(minX, x0, x1);
      maxX = Math.max(maxX, x0, x1);
      minZ = Math.min(minZ, z0, z1);
      maxZ = Math.max(maxZ, z0, z1);
      maxH = Math.max(maxH, lay.maxH);
    });

    if (!Number.isFinite(minX)) {
      minX = 0; maxX = 120; minZ = 0; maxZ = 0; maxH = 168;
    }

    const cx = (minX + maxX) / 2;
    const cz = (minZ + maxZ) / 2;
    const sx = maxX - minX || 120;
    const sz = maxZ - minZ || 1;

    return {
      layouts: lays,
      boundingCenter: { x: cx, y: (maxH || 168) / 2, z: cz },
      boundingSize: { sx: sx || 120, sy: maxH || 168, sz: sz || 1 },
    };
  }, [segments, jointWidth]);

  // Build floor polygon geometry when the chain forms a closed loop
  const floorGeo = useMemo(() => {
    if (!floorVertices || floorVertices.length < 3) return null;
    const shape = new THREE.Shape();
    shape.moveTo(floorVertices[0][0], -floorVertices[0][1]); // negate Z for Shape's Y
    for (let i = 1; i < floorVertices.length; i++) {
      shape.lineTo(floorVertices[i][0], -floorVertices[i][1]);
    }
    shape.closePath();
    return new THREE.ShapeGeometry(shape);
  }, [floorVertices]);

  const panelsKey = segments.map(s => s.elevId).join(',') + '|' + totalPanelCount;

  const panelSize = useRef({ sx: 120, sy: 168, sz: 1 });
  panelSize.current = boundingSize;
  const centerRef = useRef({ x: 0, y: 84, z: 0 });
  centerRef.current = boundingCenter;

  const gridExtent = useMemo(() => snapGridExtent(
    boundingCenter.x - boundingSize.sx / 2,
    boundingCenter.x + boundingSize.sx / 2,
    boundingCenter.z - boundingSize.sz / 2,
    boundingCenter.z + boundingSize.sz / 2,
  ), [boundingCenter, boundingSize]);

  return (
    <div className="viewer-root" tabIndex={-1}>
      <SiteViewerToolbar
        onCenterView={() => { setViewPreset(lastPresetRef.current); setResetKey(k => k + 1); }}
        onZoomIn={() => { zoomRef.current?.(0.8); clearViewPreset(); }}
        onZoomOut={() => { zoomRef.current?.(1.25); clearViewPreset(); }}
        onPanLeft={() => { panRef.current?.(30, 0); clearViewPreset(); }}
        onPanRight={() => { panRef.current?.(-30, 0); clearViewPreset(); }}
        onPanUp={() => { panRef.current?.(0, -30); clearViewPreset(); }}
        onPanDown={() => { panRef.current?.(0, 30); clearViewPreset(); }}
      />
      <Canvas
        frameloop="demand"
        camera={{ fov: 45, near: 0.1, far: 1_000_000, position: [0, 0, 500] }}
        gl={{ antialias: true }}
        shadows
        style={{ background: '#0d1117' }}
      >
        <CameraLock
          preset={ui.viewPreset}
          panelId={panelsKey}
          panelSize={panelSize}
          center={centerRef}
          resetKey={resetKey}
          zoomRef={zoomRef}
          panRef={panRef}
          onManualMove={clearViewPreset}
          onPanelClick={onPanelClick}
          onPanelDoubleClick={onPanelDoubleClick}
        />

        <ambientLight intensity={0.6} />
        <directionalLight position={[3000, 5000, 2000]} intensity={1.2} castShadow />
        <directionalLight position={[-2000, 1000, -1000]} intensity={0.4} />

        {ui.shadingMode === 'shaded' && (
          <Environment preset="warehouse" background={false} />
        )}

        {totalPanelCount > 0 ? (
          segments.map((seg, si) => {
            const lay = layouts[si];
            if (seg.panels.length === 0) return null;
            return (
              <group key={seg.elevId} position={[seg.x, 0, seg.z]} rotation={[0, seg.rotationY, 0]}>
                {seg.panels.map((p, pi) => {
                  const elev = elevMap.get(seg.elevId);
                  const trims = elev ? computeEdgeTrims(p.id, elev, jointWidth) : { leftTrim: 0, rightTrim: 0 };
                  return (
                  // Y offset aligns panels on FFE (grid = FFE datum at Y=0).
                  // botElev is feet below FFE; multiply by 12 for inches.
                  <group key={p.id} position={[lay.offsets[pi], (p.mpvar.botElev ?? 0) * 12, 0]}>
                    <PanelMesh3D panel={p} shading={ui.shadingMode} showPanelMarks={ui.showPanelMarks} isSelected={selectedSet.has(p.id)} isClash={clashPanelIds.has(p.id)} layerVisibility={ui.layerVisibility} leftTrim={trims.leftTrim} rightTrim={trims.rightTrim} />
                  </group>
                  );
                })}
              </group>
            );
          })
        ) : (
          <mesh>
            <boxGeometry args={[240, 360, 8]} />
            <meshStandardMaterial color={0x334455} wireframe />
          </mesh>
        )}

        {floorGeo && (
          <mesh geometry={floorGeo} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
            <meshStandardMaterial color="#2a2a2a" roughness={0.95} metalness={0.0} side={THREE.DoubleSide} depthWrite={false} />
          </mesh>
        )}

        <Grid
          args={[gridExtent.width, gridExtent.depth]}
          cellSize={120}
          sectionSize={600}
          cellColor="#3a5a80"
          sectionColor="#5080b0"
          cellThickness={1.5}
          sectionThickness={2.5}
          fadeDistance={8000}
          visible={ui.showGrid}
          position={[gridExtent.cx, 0, gridExtent.cz]}
        />

        {ui.showGrid && <GridLabels extent={gridExtent} />}

        {ui.showGround && (
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[gridExtent.cx, -0.1, gridExtent.cz]} receiveShadow>
            <planeGeometry args={[gridExtent.width, gridExtent.depth]} />
            <meshStandardMaterial color="#8b7355" roughness={0.95} metalness={0.0} />
          </mesh>
        )}

        <NorthArrow />

        {/* ── Clash beacons — pulsing red spotlights at each clash location ── */}
        {clashes.map((clash, ci) => (
          <ClashBeacon key={ci} x={clash.worldX} y={clash.worldY} z={clash.worldZ} severity={clash.severity} description={clash.description} />
        ))}
      </Canvas>

      {totalPanelCount === 0 && (
        <div className="viewer-no-panel">
          No panels assigned to checked elevations.
        </div>
      )}
    </div>
  );
}
